import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { api, track } from "../lib/api";
import { IMG } from "../data/content";

const FILTERS = ["All", "Celebrity Events", "Concerts", "Weddings", "Corporate Events", "Brand Launches", "Political Events", "Private Celebrations", "Media Promotions"];

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    track("portfolio_view", {});
    api.get("/portfolio").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <main>
      <PageHero
        eyebrow="Selected Experiences"
        title="Moments We Helped Bring to Life"
        description="An editorial selection of experiences across concerts, weddings, launches and celebrations."
        image={IMG.stagePurple}
      />
      <section className="py-20 lg:py-28" data-testid="portfolio-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="portfolio-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[0.65rem] uppercase tracking-[0.2em] px-4 py-2 border transition-colors duration-300 ${
                  filter === f ? "border-[#C9A55C] text-[#C9A55C] bg-[#C9A55C]/10" : "border-[#C9A55C]/20 text-[#A9ABB2] hover:border-[#C9A55C]/60 hover:text-[#F3EEE5]"
                }`}
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {f}
              </button>
            ))}
          </div>

          {loaded && visible.length === 0 ? (
            <p className="font-accent italic text-2xl text-[#A9ABB2]" data-testid="portfolio-empty">
              New experiences are being prepared for publication.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visible.map((p, i) => (
                <Reveal key={p._id || i} delay={(i % 3) * 0.07}>
                  <button onClick={() => setActive(p)} className="group block w-full text-left" data-testid={`portfolio-item-${i}`}>
                    <div className="img-frame aspect-[3/4] border border-[#C9A55C]/15">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#070708] via-transparent to-transparent" />
                      <div className="absolute bottom-0 p-6">
                        <p className="text-[0.6rem] uppercase tracking-[0.3em] text-[#C9A55C]">{p.category}{p.city ? ` · ${p.city}` : ""}</p>
                        <h3 className="font-display text-xl text-[#F3EEE5] mt-1 group-hover:text-[#C9A55C] transition-colors">{p.title}</h3>
                        {p.kind === "showcase" && (
                          <p className="text-[0.55rem] uppercase tracking-[0.25em] text-[#A9ABB2]/70 mt-2">Representative visual</p>
                        )}
                      </div>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-[60] bg-[#070708]/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-10" onClick={() => setActive(null)} data-testid="portfolio-lightbox">
          <button className="absolute top-6 right-6 text-[#F3EEE5] hover:text-[#C9A55C] transition-colors" onClick={() => setActive(null)} aria-label="Close" data-testid="lightbox-close-btn">
            <X size={28} />
          </button>
          <div className="max-w-4xl w-full grid md:grid-cols-2 gap-8 items-center" onClick={(e) => e.stopPropagation()}>
            <img src={active.image} alt={active.title} className="w-full aspect-[3/4] object-cover border border-[#C9A55C]/25" />
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#C9A55C]">{active.category}{active.city ? ` · ${active.city}` : ""}</p>
              <h3 className="font-display text-3xl text-[#F3EEE5] mt-3">{active.title}</h3>
              {active.venue && <p className="text-sm text-[#A9ABB2] mt-1">{active.venue}{active.date ? ` · ${active.date}` : ""}</p>}
              <p className="mt-5 text-sm text-[#A9ABB2] leading-relaxed">{active.description}</p>
              {active.services?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {active.services.map((s) => (
                    <span key={s} className="text-[0.6rem] uppercase tracking-[0.2em] border border-[#C9A55C]/25 text-[#F3EEE5]/80 px-3 py-1.5">{s}</span>
                  ))}
                </div>
              )}
              {active.kind === "showcase" && (
                <p className="mt-6 text-xs text-[#A9ABB2]/60 italic">Representative visual — client work is published only with permission.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
